import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { getOrgTermsContent } from '../../../content/utils.js';
import {
  navigateToChangeStrategy,
  navigateToStepDetails,
  navigateToStepMileStones,
  onReset,
} from './utils/index.js';
import { retrieveProject, selectProject } from '../../../modules/projects.js';
import StepGovernanceManuallyForm from '../../../components/projects/ChangeStrategy/ChnageStrategyForms/StepGovernanceManuallyForm.jsx';
import {
  deleteChangeStrategyDraft,
  retrieveChangeStrategy,
  selectChangeStrategy,
  selectDraftChangeStrategy,
  updateChangeStrategy,
  updateChangeStrategyDraft,
} from '../../../modules/changeStrategy.js';
import { selectSearchUsersResults } from '../../../modules/users.js';
import {
  retrieveProjectOwners,
  selectProjectOwnersWithArchived,
} from '../../../modules/projectOwners.js';
import ChangeStrategyFormHeader from '../../../components/headers/PageHeaders/Project/ChangeStrategy/ChangeStrategyFormHeader.jsx';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';
import { getOrgSlug } from '../../../lib/utils.js';
import EmployeeFormContainer from '../../employees/EmployeeFormContainer.jsx';
import { retrieveOrgGroups } from '../../../modules/orgGroups.js';

const mapStateToProps = (state, ownProps) => ({
  editing: selectChangeStrategy(state),
  changeStrategy: selectChangeStrategy(state),
  changeStrategyDraft: selectDraftChangeStrategy(state),
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
  projectOwners: selectProjectOwnersWithArchived(state) || [],
  apiUrl: state.config.apiUrl,
  searchResults: selectSearchUsersResults(state),
});

const mapDispatchToProps = {
  deleteChangeStrategyDraft,
  updateChangeStrategyDraft,
  updateChangeStrategy,
  retrieveChangeStrategy,
  retrieveProjectOwners,
  retrieveProject,
  retrieveOrgGroups,
};

function StepGovernanceFormContainer({
  projectId,
  editing,
  changeStrategyDraft,
  updateChangeStrategyDraft,
  updateChangeStrategy,
  deleteChangeStrategyDraft,
  project,
  projectOwners,
  retrieveProjectOwners,
  retrieveOrgGroups,
}) {
  const searchParams = {
    objectType: 'change_strategy',
    objectId: changeStrategyDraft ? changeStrategyDraft.id : null,
    active: !editing,
  };

  const [errors, updateErrors] = useState({ owners: null });
  const [loading, setLoading] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [key, setKey] = useState(null);

  const onHideEmployeeModal = () => {
    setKey(null);
    setShowEmployeeModal(false);
  };
  const onShowEmployeeModal = (key) => {
    setKey(key);
    setShowEmployeeModal(true);
  };

  const [entities, setEntities] = useState({
    owners: [],
    committee: [],
    sponsors: [],
    resources: [],
  });

  const updateEntities = (payload) => {
    setEntities({
      ...entities,
      ...payload,
    });
  };

  const onSelectOwner = (owners) => updateEntities({ owners });

  const onSelectEmployee = (key, employee) => {
    const isSelected = entities[key].some((e) => e.id === employee.id);

    if (isSelected) {
      if (!window.confirm('Are you sure you want to detach this employee?'))
        return;
      updateEntities({
        [key]: entities[key].filter((e) => e.id !== employee.id),
      });
    } else {
      updateEntities({ [key]: [...entities[key], employee] });
    }
  };

  const onAddEmployeeSubmitSuccess = ({ entities, result }) => {
    onSelectEmployee(key, entities.employees[result]);
  };

  const goBack = () => navigateToStepDetails(editing, projectId, getOrgSlug());
  const goNext = () =>
    navigateToStepMileStones(editing, projectId, getOrgSlug());
  const goStart = () => navigateToChangeStrategy(projectId, getOrgSlug());

  const onSubmit = (values) => {
    const { finish } = values;
    if (!entities.owners.length) {
      updateErrors({
        owners: `Should be at least one ${getOrgTermsContent('project')} Owner`,
      });
      return;
    }
    updateErrors({ owners: null });

    setLoading(true);
    const sendData = {
      owner_ids: entities.owners.map((owner) => owner.id),
      steering_committee_ids: entities.committee.map((e) => e.id),
      project_sponsor_ids: entities.sponsors.map((e) => e.id),
      supporting_resource_ids: entities.resources.map((e) => e.id),
    };

    if (finish) {
      updateChangeStrategy(
        projectId,
        { change_strategy: sendData },
        true,
        true
      ).then(() => goStart());
    } else {
      updateChangeStrategyDraft(
        projectId,
        { change_strategy: sendData },
        true,
        true
      ).then(() => goNext());
    }
  };

  const onCancel = () => {
    deleteChangeStrategyDraft(projectId).then(() => {
      navigateToChangeStrategy(projectId, getOrgSlug());
    });
  };

  useEffect(() => {
    !changeStrategyDraft && onReset(editing, projectId, getOrgSlug());
  }, []);

  useEffect(() => {
    retrieveProjectOwners({
      objectType: 'change_strategy',
      objectId: changeStrategyDraft ? changeStrategyDraft.id : null,
    });
    retrieveOrgGroups();
    if (changeStrategyDraft) {
      updateEntities({
        owners: changeStrategyDraft.owners,
        sponsors: changeStrategyDraft.project_sponsors,
        committee: changeStrategyDraft.steering_committee,
        resources: changeStrategyDraft.supporting_resources,
      });
    }
  }, []);

  useEffect(() => {
    if (entities.owners.length) {
      updateErrors({ owners: null });
    }
  }, [entities.owners]);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        editing={editing}
        subject="Change Strategy"
        typeStep="Governance"
      >
        {project && (
          <ChangeStrategyFormHeader
            slug={getOrgSlug()}
            project={project}
            editing={editing}
          />
        )}
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  {project && (
                    <Form
                      onSubmit={onSubmit}
                      projectOwners={projectOwners.map((owner) => ({
                        ...owner,
                        id: owner.id,
                        name: owner.fullName,
                        children: [],
                      }))}
                      entities={entities}
                      editing={editing}
                      loading={loading}
                      errorsEmpl={errors}
                      onBack={goBack}
                      onCancel={onCancel}
                      onSelectOwner={onSelectOwner}
                      onSelectEmployee={onSelectEmployee}
                      onShowEmployeeModal={onShowEmployeeModal}
                      employeeSearchParams={searchParams}
                      component={StepGovernanceManuallyForm}
                    />
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        <EmployeeFormContainer
          showFormModal={showEmployeeModal}
          onHideFormModal={onHideEmployeeModal}
          onSubmitSuccess={onAddEmployeeSubmitSuccess}
        />
      </Grid>
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepGovernanceFormContainer);
